import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { LLMModel, llmModelService } from './llmModelService';

const LLMModelManagement: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [notAuthorized, setNotAuthorized] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [models, setModels] = useState<LLMModel[]>([]);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);

  const loadModels = async () => {
    try {
      setLoading(true);
      const response = await llmModelService.getAllModels();
      setModels(response.models);
      setLastSyncedAt(response.lastSyncedAt);
      setError(null);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setNotAuthorized(true);
      } else {
        setError('Failed to load models. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModels();
  }, []);

  const dismissMessages = () => {
    setTimeout(() => {
      setError(null);
      setSuccess(null);
    }, 6000);
  };

  const handleToggleActive = async (model: LLMModel) => {
    try {
      const updated = await llmModelService.setModelActive(model.id, !model.isActive);
      setModels(models.map(m => (m.id === updated.id ? updated : m)));
      setSuccess(`Model ${updated.modelId} ${updated.isActive ? 'activated' : 'deactivated'}.`);
      dismissMessages();
    } catch (err: any) {
      const reason = err?.response?.data?.reason ?? 'Failed to update model.';
      setError(reason);
      dismissMessages();
    }
  };

  const handleApprovePricing = async (model: LLMModel, pricingId: string) => {
    try {
      await llmModelService.activatePricing(model.id, pricingId);
      setSuccess(`New pricing approved for ${model.modelId}.`);
      await loadModels();
      dismissMessages();
    } catch (err) {
      setError('Failed to activate pricing.');
      dismissMessages();
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const summary = await llmModelService.triggerSync();
      setSuccess(
        `Sync complete: ${summary.newModels.length} new model(s), ${summary.updatedModels} updated, ${summary.pendingPricingChanges} pricing change(s) awaiting review.`
      );
      await loadModels();
      dismissMessages();
    } catch (err) {
      setError('Sync with models.dev failed.');
      dismissMessages();
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <Container maxW="6xl">
        <Box py={10}>
          <Flex justifyContent="center" p={6}>
            <Spinner size="lg" />
          </Flex>
        </Box>
      </Container>
    );
  }

  if (notAuthorized) {
    return (
      <Container maxW="6xl">
        <Box py={10}>
          <Heading as="h1" size="lg" color="gray.900">Model Administration</Heading>
          <Text color="red.700" mt={4}>
            You are not authorized to view this page. Admin access is required.
          </Text>
        </Box>
      </Container>
    );
  }

  const activeCount = models.filter(m => m.isActive).length;
  const pendingPricingCount = models.filter(m => m.pendingPricings.length > 0).length;
  const providers = Array.from(new Set(models.map(m => m.providerDisplayName)));

  return (
    <Container maxW="6xl">
      <Box py={{ base: 6, md: 10 }}>
        <HStack justify="space-between" align="flex-start" mb={6} flexWrap="wrap" gap={3}>
          <Box>
            <Heading as="h1" size="lg" color="gray.900">LLM Model Administration</Heading>
            <Text color="gray.600" mt={1}>
              Review and enable models synced from models.dev. Active models are available to proxy users.
            </Text>
            <Text color="gray.500" fontSize="sm" mt={1}>
              {models.length} models across {providers.length} providers · {activeCount} active ·{' '}
              {pendingPricingCount} with pending pricing changes
              {lastSyncedAt ? ` · last synced ${new Date(lastSyncedAt).toLocaleString()}` : ' · never synced'}
            </Text>
          </Box>
          <Button onClick={handleSync} colorScheme="blue" isLoading={syncing} loadingText="Syncing...">
            Sync Now
          </Button>
        </HStack>

        {error && (
          <Box mb={4} p={4} borderWidth="1px" borderColor="red.100" borderRadius="xl" bg="red.50" boxShadow="sm">
            <Text color="red.700">{error}</Text>
          </Box>
        )}

        {success && (
          <Box mb={4} p={4} borderWidth="1px" borderColor="green.100" borderRadius="xl" bg="green.50" boxShadow="sm">
            <Text color="green.700">{success}</Text>
          </Box>
        )}

        {models.length === 0 ? (
          <Box borderWidth="1px" borderColor="gray.200" borderRadius="xl" bg="white" boxShadow="sm" p={6}>
            <Text color="gray.700">
              No models in the catalog yet. Run a sync to import the latest models from models.dev.
            </Text>
          </Box>
        ) : (
          <Box borderWidth="1px" borderColor="gray.200" borderRadius="xl" overflow="hidden" bg="white" boxShadow="sm">
            <Box as="table" width="100%" style={{ borderCollapse: 'collapse' }}>
              <Box as="thead" bg="gray.50">
                <Box as="tr">
                  <Box as="th" p={3} textAlign="left">Provider</Box>
                  <Box as="th" p={3} textAlign="left">Model</Box>
                  <Box as="th" p={3} textAlign="left">Pricing ($/M tokens in / out)</Box>
                  <Box as="th" p={3} textAlign="left">Utokens (in / out)</Box>
                  <Box as="th" p={3} textAlign="left">Status</Box>
                  <Box as="th" p={3} textAlign="right">Actions</Box>
                </Box>
              </Box>
              <Box as="tbody">
                {models.map(model => (
                  <Box as="tr" key={model.id} borderTopWidth="1px" _hover={{ bg: 'gray.50' }}>
                    <Box as="td" p={3} color="gray.600">{model.providerDisplayName}</Box>
                    <Box as="td" p={3}>
                      <Text fontWeight="medium">{model.displayName}</Text>
                      <Text color="gray.500" fontSize="sm" fontFamily="mono">{model.modelId}</Text>
                      {model.upstreamStatus && (
                        <Badge colorScheme="orange" variant="subtle" px={2} py={0.5} borderRadius="full" mr={1} fontSize="xs">
                          {model.upstreamStatus}
                        </Badge>
                      )}
                    </Box>
                    <Box as="td" p={3}>
                      {model.activePricing ? (
                        <Text fontFamily="mono" fontSize="sm">
                          {model.activePricing.inputCostPerMillion} / {model.activePricing.outputCostPerMillion}
                        </Text>
                      ) : (
                        <Text color="gray.500" fontSize="sm">No active pricing</Text>
                      )}
                      {model.pendingPricings.map(pending => (
                        <Box key={pending.id} mt={1}>
                          <Text color="orange.700" fontFamily="mono" fontSize="sm">
                            pending: {pending.inputCostPerMillion} / {pending.outputCostPerMillion}
                          </Text>
                          <Button
                            size="xs"
                            colorScheme="orange"
                            variant="outline"
                            mt={1}
                            onClick={() => handleApprovePricing(model, pending.id)}
                          >
                            Approve New Pricing
                          </Button>
                        </Box>
                      ))}
                    </Box>
                    <Box as="td" p={3} fontFamily="mono" fontSize="sm">
                      {model.activePricing
                        ? `${model.activePricing.inputTokensPerUniversal} / ${model.activePricing.outputTokensPerUniversal}`
                        : '—'}
                    </Box>
                    <Box as="td" p={3}>
                      <Badge
                        colorScheme={model.isActive ? 'green' : 'gray'}
                        variant="subtle"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        {model.isActive ? 'Active' : model.activePricing ? 'Pending review' : 'No pricing'}
                      </Badge>
                    </Box>
                    <Box as="td" p={3} textAlign="right">
                      <Button
                        size="sm"
                        colorScheme={model.isActive ? 'red' : 'green'}
                        onClick={() => handleToggleActive(model)}
                        isDisabled={!model.isActive && !model.activePricing && model.pendingPricings.length === 0}
                      >
                        {model.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default LLMModelManagement;
